import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { pipe, find, pathEq, propEq } from 'ramda'
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import useRole from '@/hooks/use-role'
import { breakpoints } from '@/styles'
import { Button } from '@/components/button'
import { fontWeights, colors } from '@/styles'
import { handlePDFView } from './pdf-view'

const isDraft = propEq('state_machine', 'draft')

const TableContentButton = styled(Button)`
  width: 120px;
  height: 34px;
  justify-content: center;
  align-self: center;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const CountdownContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: center;
  height: 34x;
  width: 34px;
  font-size: 11px;
  font-weight: ${fontWeights.medium};
  color: ${colors.grey88};
  margin-bottom: 5px;
`

const CountPosition = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
`

const SendCountdown = ({ countDown, countDownLimit }) => {
  return (
    <CountPosition>
      <CountdownContainer>
        <CircularProgressbarWithChildren
          value={countDown}
          minValue={0}
          maxValue={countDownLimit}
          counterClockwise={false}
          strokeWidth={5}
          styles={buildStyles({
            pathColor: `${colors.grey88}`,
            trailColor: `${colors.white}`,
            textColor: `${colors.grey88}`,
            pathTransitionDuration: 0.5
          })}
        >
          {countDown}
        </CircularProgressbarWithChildren>
      </CountdownContainer>
    </CountPosition>
  )
}

const ActionButton = ({
  contract,
  packageFile,
  order,
  signers = [],
  countDown,
  countDownLimit,
  canResend,
  setCanResend
}) => {
  const history = useHistory()
  const { t } = useTranslation()
  const role = useRole()
  const dispatch = useDispatch()
  const state_machine = packageFile?.state_machine
  const isContractActive = state_machine === 'active'
  const isContractCreated = state_machine === 'created'
  const isContractSent = state_machine === 'sent'
  const isContractSentNotSigned =
    state_machine === 'sent' && packageFile?.signature_status === 'pending'
  const isFileSigned = packageFile?.state_machine === 'signed'

  const landlordSigned = pipe(
    find(pathEq(['metadata', 'is_creator'], true)),
    propEq('state_machine', 'signed')
  )(signers)

  const isDisabled = !isContractCreated && !isContractSent
  const theme = isContractSent ? 'secondary' : 'primary'
  const viewContract = () => {
    const getNewUrl = (role, contract) => {
      const where = role === 'tenant' ? 'rental' : 'listing'
      if (isDraft(contract))
        return `/listing/${contract?.listing_id}/contract/create/${
          contract?.listing_contract_id || ''
        }`
      return `/${where}/${contract?.listing_id}/contract/${contract?.id}`
    }
    history.push(getNewUrl(role, contract))
  }

  const handleContractView = () => handlePDFView(contract?.id, packageFile.id)

  const sendContract = async () => {
    if (canResend) {
      await dispatch.contract.sendCustomDocument({
        contractId: contract?.id,
        packageFileId: packageFile?.id
      })
    }
    setCanResend(false)
  }

  const signContract = () => history.push(`/custom-contract/sign/${contract?.id}/${order}`)

  if (isContractActive) {
    return (
      <TableContentButton theme="secondary" onClick={viewContract} size="small" space="none">
        {t('l.contract_manager.documents.view.button', 'View')}
      </TableContentButton>
    )
  }
  if (isFileSigned) {
    return (
      <TableContentButton theme="secondary" onClick={handleContractView} size="small" space="none">
        {t('l.contract_manager.documents.view.button', 'View')}
      </TableContentButton>
    )
  }

  if (landlordSigned) {
    return (
      <>
        {canResend ? (
          <TableContentButton
            onClick={sendContract}
            disabled={isDisabled || !canResend}
            theme="secondary"
            size="small"
            space="none"
          >
            {t('l.contract_manager.documents.resend.button', 'Resend')}
          </TableContentButton>
        ) : (
          <SendCountdown countDown={countDown} countDownLimit={countDownLimit} />
        )}
      </>
    )
  }
  if (isContractSentNotSigned) {
    return (
      <TableContentButton
        onClick={signContract}
        disabled={isDisabled}
        theme="primary"
        size="small"
        space="none"
      >
        {t('l.contract_manager.documents.sign.button', 'Sign')}
      </TableContentButton>
    )
  }

  return !canResend ? (
    <SendCountdown countDown={countDown} countDownLimit={countDownLimit} />
  ) : (
    <TableContentButton
      onClick={sendContract}
      disabled={isDisabled}
      theme={theme}
      size="small"
      space="none"
    >
      {t('l.contract_manager.documents.send.button', 'Send')}
    </TableContentButton>
  )
}

export default ActionButton
